import { z } from 'zod';
import { archiveFilterSchema, baseListQuery, dateOnlySchema, uuidSchema } from './common';
import {
  projectByAudiencePolicy,
  type AudiencePolicy,
  type ServiceRequestAudience,
  type ServiceRequestStatus,
} from './service-requests';

// ── Справочник оргтехники (ADR 0085) ──
// Единица: тип, модель, номера, место (объект и уточнение внутри него), отдел-владелец, гарантия
// поставщика. Заявки на обслуживание ссылаются на единицу и хранят снимок её реквизитов, поэтому
// правка карточки прошлое не переписывает.

// ── Тип оргтехники ──
// Перечень, как типы контейнеров: код, название, порядок. Ведётся в окне из вкладки справочника —
// сам по себе тип ничего не значит, и отдельная вкладка ради десяти строк не заводится.

export const OFFICE_EQUIPMENT_TYPE_SORT_FIELDS = ['name', 'code', 'sortOrder', 'isActive'] as const;

const typeCodeSchema = z
  .string()
  .trim()
  .min(2, 'Код типа — минимум 2 символа')
  .max(50)
  .regex(/^[a-z0-9_]+$/, 'Код типа — латиница, цифры и подчёркивание');

const typeNameSchema = z.string().trim().min(2, 'Укажите название типа').max(255);

export const officeEquipmentTypeListQuerySchema = baseListQuery(
  OFFICE_EQUIPMENT_TYPE_SORT_FIELDS,
).extend({
  isActive: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});

export const createOfficeEquipmentTypeSchema = z.object({
  code: typeCodeSchema,
  name: typeNameSchema,
  sortOrder: z.number().int().min(0).max(9999).optional().default(100),
  isActive: z.boolean().optional().default(true),
});
export type CreateOfficeEquipmentTypeInput = z.infer<typeof createOfficeEquipmentTypeSchema>;

// `.partial()` снимает обязательность, но не `.default()` — поля со значением по умолчанию
// объявляются заново, иначе PATCH без поля затирал бы значение (тот же подвох, что у складов).
export const updateOfficeEquipmentTypeSchema = createOfficeEquipmentTypeSchema.partial().extend({
  sortOrder: z.number().int().min(0).max(9999).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateOfficeEquipmentTypeInput = z.infer<typeof updateOfficeEquipmentTypeSchema>;

export interface OfficeEquipmentTypeDto {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── Местонахождение единицы (план модернизации, Р61) ──
//
// Не «статус жизненного цикла»: списание и ввод в эксплуатацию модуль не ведёт (§12 плана модуля),
// а `isActive` отвечает на другой вопрос — «эксплуатируется ли». Здесь только физическое место, и
// меняется оно перемещением, а не правкой карточки.

export const OFFICE_EQUIPMENT_STATES = [
  'on_site',
  'at_service',
  'in_stock',
  'with_employee',
] as const;
export const officeEquipmentStateSchema = z.enum(OFFICE_EQUIPMENT_STATES);
export type OfficeEquipmentState = (typeof OFFICE_EQUIPMENT_STATES)[number];

export const officeEquipmentStateLabels: Record<OfficeEquipmentState, string> = {
  on_site: 'На объекте',
  at_service: 'В ремонте',
  in_stock: 'На складе',
  with_employee: 'У сотрудника',
};

export const officeEquipmentStateColors: Record<OfficeEquipmentState, string | undefined> = {
  // «На объекте» — рабочее состояние, и цветом его не выделяют: иначе в списке горит каждая строка.
  on_site: undefined,
  at_service: 'orange',
  in_stock: 'blue',
  with_employee: 'geekblue',
};

/**
 * A unit's place in words — "code — site · room · state (note)", empty parts dropped (ADR 0215).
 *
 * One wording for the portal and for letters: a contractor reads the letter, an executor reads the
 * request card, and the two must name the same place the same way. The site goes with its code
 * because site names repeat ("Склад"). "On site" is the working state and is not spelled out —
 * only its note, when there is one.
 */
export function officeEquipmentPlaceWords(place: {
  objectCode: string;
  objectName: string;
  location: string;
  state: OfficeEquipmentState;
  stateNote: string;
}): string {
  const state =
    place.state === 'on_site'
      ? place.stateNote
      : `${officeEquipmentStateLabels[place.state].toLowerCase()}${
          place.stateNote ? ` (${place.stateNote})` : ''
        }`;
  return [`${place.objectCode} — ${place.objectName}`, place.location, state]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Состояния, которые обязаны быть уточнены (то же держит CHECK в БД): «на складе» и «у сотрудника»
 * без уточнения — потерянная техника, искать её по такой записи негде. У «на объекте» место уже
 * есть колонкой `location`, у «в ремонте» — сервисная компания в заявке.
 */
export function officeEquipmentStateNeedsNote(state: OfficeEquipmentState): boolean {
  return state === 'in_stock' || state === 'with_employee';
}

// ── Единица оргтехники ──

export const OFFICE_EQUIPMENT_SORT_FIELDS = [
  'name',
  'type',
  'object',
  'inventoryNumber',
  'serialNumber',
  'warrantyUntil',
  'isActive',
  'createdAt',
] as const;

/** Номера свободного вида: их печатает производитель и клеит бухгалтерия, формата у них нет. */
const numberSchema = z.string().trim().max(100);
const locationSchema = z.string().trim().max(255);
const commentSchema = z.string().trim().max(2000);

/**
 * Фильтр по гарантии: три вопроса, которые задают этому справочнику. `expiring` — «что продлевать в
 * ближайший месяц», и порог у него общий с подсветкой (`WARRANTY_EXPIRING_DAYS`).
 */
export const OFFICE_EQUIPMENT_WARRANTY_FILTERS = ['active', 'expiring', 'expired'] as const;
export type OfficeEquipmentWarrantyFilter = (typeof OFFICE_EQUIPMENT_WARRANTY_FILTERS)[number];

export const officeEquipmentListQuerySchema = baseListQuery(OFFICE_EQUIPMENT_SORT_FIELDS).extend({
  objectId: uuidSchema.optional(),
  equipmentTypeId: uuidSchema.optional(),
  /**
   * «Вся техника этой модели» — так из карточки расходника и из окна моделей приходят к самим
   * аппаратам. Фильтр по ссылке, а не по наименованию: имя карточки с выпуска A — зеркало модели,
   * и поиск по нему нашёл бы то же самое, но развалился бы на первом же переименовании.
   */
  modelId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  state: officeEquipmentStateSchema.optional(),
  /**
   * «В ремонте, а открытых заявок нет» — срез «Требуют внимания» (Р61). Портал не знает, вернули
   * ли аппарат: он знает только то, что ему сказали, — поэтому это отчёт, а не запрет.
   */
  strandedAtService: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  /** «Не закреплена ни за кем» — срез для разметки парка; с `departmentId` не сочетается. */
  unassignedDepartment: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  warranty: z.enum(OFFICE_EQUIPMENT_WARRANTY_FILTERS).optional(),
  isActive: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  archive: archiveFilterSchema,
});

const stateNoteSchema = z.string().trim().max(255);

const equipmentFields = {
  equipmentTypeId: uuidSchema,
  /**
   * The model as a directory record (ADR 0132) — required since release B, together with
   * `NOT NULL` on `office_equipment.model_id`. Release A accepted a card without it for the
   * rollout window of code that knew nothing about models.
   *
   * The model must be of the card's type: the composite "lock" key holds the pair, and the route
   * refuses a foreign type with 422 instead of letting the key answer with a constraint name.
   */
  modelId: uuidSchema,
  /**
   * The card's name is not an input: it is the model's name, kept by the database mirror (0171).
   * Release A still took it as a way to name the model in words; release B removed that door.
   *
   * Refused rather than silently dropped, for the reason the consumable's `quantity` is
   * (`updateOfficeEquipmentConsumableSchema`): a `PATCH { name }` that answered 200 and kept the
   * card on its old model would tell the person "saved" about a change that never happened — the
   * very silent edit decision 4 of ADR 0132 exists to rule out. No portal bundle the prod floor
   * admits sends the field (all of them build the body with `officeEquipmentPayload`).
   */
  name: z
    .never({ error: 'Наименование карточки задаёт модель: выберите модель из справочника' })
    .optional(),
  serialNumber: numberSchema.optional().default(''),
  inventoryNumber: numberSchema.optional().default(''),
  objectId: uuidSchema,
  departmentId: uuidSchema.nullish(),
  location: locationSchema.optional().default(''),
  purchasedOn: dateOnlySchema.nullish(),
  warrantyUntil: dateOnlySchema.nullish(),
  comment: commentSchema.optional().default(''),
  isActive: z.boolean().optional().default(true),
};

/**
 * At least one number is required (the database holds the same CHECK): the unit is identified when
 * it comes back from repair, and "an MFP without numbers" in an act is indistinguishable from the
 * one next to it.
 */
const IDENTITY_MESSAGE = 'Укажите серийный или инвентарный номер';

export const createOfficeEquipmentSchema = z
  .object(equipmentFields)
  .refine((v) => !!v.serialNumber || !!v.inventoryNumber, {
    message: IDENTITY_MESSAGE,
    path: ['inventoryNumber'],
  });
export type CreateOfficeEquipmentInput = z.infer<typeof createOfficeEquipmentSchema>;

/**
 * Правка карточки объект больше не меняет (Р59): переезд — событие с датой, причиной и обеими
 * сторонами, и тихая смена площадки в форме оставляла бы вопрос «где этот аппарат стоял в мае» без
 * ответа. Поле уезжает в свою ручку `POST /office-equipment/:id/move`.
 *
 * Отдел-владелец при этом остаётся здесь: разметка парка (кто за что отвечает) — не переезд, и
 * писать перемещение там, где технику никуда не везли, значило бы засорять журнал.
 */
export const updateOfficeEquipmentSchema = z
  .object({
    ...equipmentFields,
    objectId: z.never().optional(),
    serialNumber: numberSchema.optional(),
    inventoryNumber: numberSchema.optional(),
    location: locationSchema.optional(),
    comment: commentSchema.optional(),
    isActive: z.boolean().optional(),
  })
  .partial()
  // Правку в «оба номера пусты» ловит сервер: в PATCH приходит только изменённое, и схема одна из
  // двух колонок не видит. Здесь отсекается лишь явная попытка стереть оба разом.
  .refine((v) => v.serialNumber !== '' || v.inventoryNumber !== '', {
    message: IDENTITY_MESSAGE,
    path: ['inventoryNumber'],
  });
export type UpdateOfficeEquipmentInput = z.infer<typeof updateOfficeEquipmentSchema>;

/** Тип в строке единицы: столько, сколько нужно для показа и повторного выбора. */
export interface OfficeEquipmentTypeRefDto {
  id: string;
  name: string;
  isActive: boolean;
}

/** Модель в строке единицы: столько, сколько нужно для показа и повторного выбора. */
export interface OfficeEquipmentModelRefDto {
  id: string;
  name: string;
}

/**
 * Значение характеристики: полное написание и сокращение для строки списка
 * (план `docs/office-equipment-specs-plan.md`, Р8).
 *
 * Сокращение приходит с сервера, а не собирается в портале: «как это называется коротко» —
 * свойство самого значения (колонка `short_name`), и вторая запись этого правила на клиенте
 * разошлась бы с первой на первой же правке перечня.
 */
export interface OfficeEquipmentSpecValueRefDto {
  id: string;
  code: string;
  /** «Цветная», «Чёрно-белая» — карточка единицы и файл обмена показывают его. */
  name: string;
  /** «цв.», «ч/б» — вторая строка колонки «Тип». */
  shortName: string;
}

/**
 * Характеристика модели в строке списка и в карточке (Р1, Р3, Р4).
 *
 * Приходят **все характеристики, положенные типу**, а не только заполненные, и это содержательно:
 * `value: null` означает «вопрос законен, ответа нет» — то самое «н/д», которое просил заказчик.
 * Характеристики, не привязанной к типу, в массиве нет вовсе, и у монитора он пуст — у монитора
 * не «н/д», у монитора вопроса нет.
 *
 * Значение хранится отсутствием строки в базе, а не третьим значением перечня (Р3): два способа
 * сказать «неизвестно» разошлись бы на первом же отборе.
 */
export interface OfficeEquipmentModelSpecDto {
  specId: string;
  /** Системный ключ характеристики: `print_color`. По нему её находят обмен файлом и тесты. */
  code: string;
  /** «Цветность печати» — подпись строки в карточке единицы. */
  name: string;
  /** Идёт ли характеристика второй строкой в колонке «Тип» (Р8, Р14). */
  showInList: boolean;
  sortOrder: number;
  /** Что выбрано у этой модели; `null` — «н/д». */
  value: OfficeEquipmentSpecValueRefDto | null;
}

/**
 * Характеристика с перечнем допустимых значений: этим отвечает `GET /office-equipment-types/:id/specs`, и по
 * нему форма модели строит своё поле выбора.
 *
 * Отдельным ответом, а не полем в каждой строке списка: значения на выбор нужны ровно там, где
 * значение вводят, а в списке техники они ехали бы копией в каждой из 353 строк. Форма же
 * спрашивает их и до того, как модель существует, — при заведении новой.
 */
export interface OfficeEquipmentSpecDto {
  id: string;
  code: string;
  name: string;
  showInList: boolean;
  sortOrder: number;
  /** Заведённые значения — тем же порядком, что в справочнике. */
  values: OfficeEquipmentSpecValueRefDto[];
}

/** Как «нет данных» выглядит в строке списка. Одно место на портал, письма и обмен файлом. */
export const OFFICE_EQUIPMENT_SPEC_UNKNOWN = 'н/д';

/**
 * Вторая строка колонки «Тип»: сокращения характеристик, помеченных `showInList` (Р8).
 *
 * Пустая строка означает «второй строки здесь нет вовсе» — так отвечает тип, у которого
 * характеристик не спрашивают. Заполненный тип без значения отвечает «н/д»: у него характеристика
 * есть, а ответа нет, и молчание вместо этого читалось бы как «чёрно-белый».
 *
 * Разделитель — «·», тот же, что между моделью и номером в `officeEquipmentTitle`: пометок в
 * строке со временем станет больше одной, и разъезжаться им незачем.
 */
export function officeEquipmentSpecLine(specs: readonly OfficeEquipmentModelSpecDto[]): string {
  return specs
    .filter((s) => s.showInList)
    .map((s) => s.value?.shortName ?? OFFICE_EQUIPMENT_SPEC_UNKNOWN)
    .join(' · ');
}

/**
 * Расходник в карточке аппарата (Р15): чем его заправлять и сколько таких на складе.
 *
 * Срез, а не карточка расходника: на вопрос «чем заправить вот этот» отвечают код (по нему заказ),
 * наименование, цвет (у цветной серии позиций несколько, и различает их именно он) и остаток —
 * «12» и «0» означают разные действия. Всё остальное — совместимость с другими моделями, журнал
 * остатка, комментарий — живёт в самой карточке расходника, и тащить его в каждую строку значило
 * бы отвечать на вопрос, которого здесь не задают.
 *
 * Своим типом, а не `Pick<OfficeEquipmentConsumableDto, …>`: контракт расходников импортирует
 * `OfficeEquipmentModelRefDto` отсюда, и обратная ссылка замкнула бы файлы друг на друга.
 */
export interface OfficeEquipmentConsumableRefDto {
  id: string;
  code: string;
  name: string;
  /** Цвет позиции или `null` у чёрно-белой (Р5). */
  color: string | null;
  /** Сколько таких на складе прямо сейчас. */
  quantity: number;
}

/** Объект в строке единицы: код различает одноимённые корпуса. */
export interface OfficeEquipmentObjectRefDto {
  id: string;
  code: string;
  name: string;
}

export interface OfficeEquipmentDepartmentRefDto {
  id: string;
  code: string;
  name: string;
}

/** Действующая гарантия на выполненную позицию ремонта — строка в истории обслуживания. */
export interface OfficeEquipmentItemWarrantyDto {
  itemId: string;
  name: string;
  warrantyUntil: string;
}

/**
 * Заявка на обслуживание в карточке единицы (§8.2): что с этим аппаратом уже делали.
 *
 * Не ссылка на список, а короткий срез: оператор смотрит карточку перед назначением сервиса, и
 * вопрос у него один — чинили ли уже и что именно меняли. Итог приходит только у закрытых заявок,
 * гарантии — только по выполненным позициям (Р12).
 */
export interface OfficeEquipmentServiceEntryDto {
  id: string;
  displayNumber: string;
  status: ServiceRequestStatus;
  createdAt: string;
  completedAt: string | null;
  serviceName: string | null;
  totalAmount: number | null;
  warranties: OfficeEquipmentItemWarrantyDto[];
}

/**
 * Что видит заявитель в «Истории обслуживания» карточки единицы (ADR 0160, решение 10; план
 * `office-equipment-requester-card-plan.md`, Р13).
 *
 * ЭТО САМЫЙ НЕОЧЕВИДНЫЙ КАНАЛ. Про карточку заявки помнят все, про эту строку — никто: вход сюда
 * открывает `officeEquipment.read`, которое есть у заказчика, а сумма ремонта стоит прямо в строке.
 * Заявитель, которому карточка заявки уже ничего не показывает, читал бы ту же сумму через
 * справочник.
 *
 * Карта, а не одиночный `totalAmount: null` в маршруте: `-?` требует решения на КАЖДОЕ поле, и
 * новое денежное поле строки (скидка, аванс, стоимость запчастей) ломает компиляцию, пока
 * разработчик не выберет аудиторию. Одиночная замена была бы fail-open — ровно тот класс ошибки,
 * ради которого весь план и написан.
 *
 * `warranties` остаются целиком: цен в строке гарантии нет ни одной, а обращаться по гарантии
 * будет как раз заявитель (матрица §4.1 — гарантийный талон ему виден). Это та же граница, что у
 * реестра гарантий (Г2): план убирает деньги, а не сам факт «что чинили».
 */
export const OFFICE_EQUIPMENT_SERVICE_ENTRY_AUDIENCE = {
  id: 'all',
  displayNumber: 'all',
  status: 'all',
  createdAt: 'all',
  completedAt: 'all',
  serviceName: 'all',
  // Итог по акту — единственные деньги строки. `null`, а не `0`: «ремонт был бесплатным» и «сумму
  // не показываем» — разные вещи, и портал рисует по `null` пустую ячейку, а не честный ноль.
  totalAmount: { requester: null },
  warranties: 'all',
} satisfies AudiencePolicy<OfficeEquipmentServiceEntryDto>;

/** Строка истории обслуживания в объёме аудитории ЭТОЙ заявки (Р13). */
export function projectOfficeEquipmentServiceEntry(
  entry: OfficeEquipmentServiceEntryDto,
  audience: ServiceRequestAudience,
): OfficeEquipmentServiceEntryDto {
  return projectByAudiencePolicy(entry, audience, OFFICE_EQUIPMENT_SERVICE_ENTRY_AUDIENCE);
}

/** Одно перемещение единицы: откуда, куда, когда и почему (Р59). */
export interface OfficeEquipmentMovementDto {
  id: string;
  movedOn: string;
  fromObject: OfficeEquipmentObjectRefDto;
  toObject: OfficeEquipmentObjectRefDto;
  fromDepartment: OfficeEquipmentDepartmentRefDto | null;
  toDepartment: OfficeEquipmentDepartmentRefDto | null;
  fromLocation: string;
  toLocation: string;
  fromState: OfficeEquipmentState;
  toState: OfficeEquipmentState;
  /**
   * Уточнение состояния обеими сторонами (план `docs/office-equipment-move-from-request-plan.md`,
   * Р5, находка Н5). Без них журнал отвечает «была у сотрудника», не отвечая, у какого: карточка
   * хранит только текущее значение, и вопрос «у кого техника была в марте» не читается ниоткуда.
   */
  fromStateNote: string;
  toStateNote: string;
  reason: string;
  comment: string;
  /** Заявка, из-за которой единицу увезли или вернули; `null` — переезд сам по себе. */
  serviceRequestId: string | null;
  serviceRequestNum: number | null;
  /**
   * «Этим действием подтверждено заявленное место» (Р8). Ставится только вместе с заявкой; у всех
   * строк журнала, записанных до выпуска, — `false`, и очередь расхождений ведёт себя как раньше.
   */
  confirmsDeclaredPlace: boolean;
  movedByName: string;
  createdAt: string;
}

/**
 * «Откуда» — исходное состояние карточки, каким его показали человеку (Р3).
 *
 * Сверяется сервером под блокировкой строки и при расхождении отвечает `409`
 * (`OFFICE_EQUIPMENT_MOVE_CONFLICT_CODE`): между открытием окна и нажатием кнопки аппарат мог
 * переехать, и записанное вторым перемещение оставило бы в журнале две ветки из одного «откуда» —
 * `A → B` и `A → C` при карточке в `C` (находка Н1).
 *
 * ПОЧЕМУ МЕСТО, А НЕ НОМЕР ВЕРСИИ. Человек видит и подтверждает именно место, и сверка того, что
 * видно, объясняет отказ словами, которые можно проверить глазами: «техника уже переехала: сейчас
 * там-то». Версии у карточки парка нет вовсе, и заводить её ради одной операции значило бы
 * повесить колонку на все остальные правки справочника.
 *
 * Все пять полей, включая пустую строку уточнения: «был у Иванова» и «был у сотрудника без
 * уточнения» — разные состояния, и сверка, закрывающая на это глаза, пропустила бы ровно ту
 * правку, ради записи которой Р5 и заводит `state_note` в журнале.
 */
export const moveSideSchema = z.object({
  objectId: uuidSchema,
  departmentId: uuidSchema.nullable(),
  location: locationSchema,
  state: officeEquipmentStateSchema,
  stateNote: stateNoteSchema,
});
export type MoveOfficeEquipmentSide = z.infer<typeof moveSideSchema>;

/**
 * Код отказа «техника уже переехала» (Р3). Своим кодом, а не общим `version_conflict`: тот портал
 * показывает как «обновите данные и повторите», а здесь человеку нужно знать, КУДА аппарат уехал —
 * исход у отказа другой (перечитать карточку и решить заново), и различить два исхода на один код
 * было бы нечем.
 */
export const OFFICE_EQUIPMENT_MOVE_CONFLICT_CODE = 'equipment_moved';

/**
 * Где единица стоит СЕЙЧАС — тело отказа `equipment_moved` (Р3).
 *
 * Названия сторон, а не одни идентификаторы: тело читает человек через окно, и «объект
 * 7f3c…-…» не отвечает на вопрос, ради которого отказ и объясняют. Идентификаторы рядом остаются —
 * ими портал перезаполняет форму и отправляет повтор с обновлённым `from`.
 */
export interface OfficeEquipmentMovePlaceDto {
  objectId: string;
  /** «СМР-2 · Стройка на Ленина»: код различает одноимённые корпуса — как в списке техники. */
  objectName: string;
  departmentId: string | null;
  departmentName: string | null;
  location: string;
  state: OfficeEquipmentState;
  stateNote: string;
}

/** Тело `409 equipment_moved`: то, из чего окно собирает фразу «сейчас техника вот здесь». */
export interface OfficeEquipmentMoveConflictDetails {
  current: OfficeEquipmentMovePlaceDto;
}

/**
 * Перемещение (Р59, Р60). Область проверяется по **исходной** стороне: перемещение — утрата, а не
 * захват, и требование «обе стороны в области» сделало бы штатный перенос между площадками
 * невозможным именно для того, кто технику отдаёт.
 */
export const moveOfficeEquipmentSchema = z
  .object({
    /**
     * Сверка исходной стороны (Р3). НЕОБЯЗАТЕЛЬНА — это выпуск A, а не половинчатость: всё время
     * раскатки в браузерах работает прежний портал, который поля не шлёт, и требовать его сразу
     * значило бы отбивать каждое перемещение до последнего обновлённого клиента. Присланный `from`
     * при этом сверяется с первого дня. Обязательным поле станет выпуском B (§7 плана), тем же
     * приёмом, что `modelId` в карточке техники.
     *
     * Защита от гонки от этого поля не зависит и зависеть не должна: строка карточки берётся
     * `FOR UPDATE` первым шагом транзакции (Р4) при любом теле — блокировка сериализует две записи,
     * а разошедшийся `from` объясняет отказ словами.
     */
    from: moveSideSchema.optional(),
    objectId: uuidSchema,
    departmentId: uuidSchema.nullish(),
    location: locationSchema.optional().default(''),
    state: officeEquipmentStateSchema.optional().default('on_site'),
    stateNote: stateNoteSchema.optional().default(''),
    movedOn: dateOnlySchema,
    reason: z.string().trim().min(3, 'Укажите причину перемещения').max(1000),
    comment: z.string().trim().max(1000).optional().default(''),
    /** Переезд из-за ремонта: «увезли в сервис» и «вернулась» заводятся из карточки заявки. */
    serviceRequestId: uuidSchema.nullish(),
    /**
     * «Подтверждаю заявленное место» (Р8): этим перемещением разобрано расхождение, о котором
     * сообщил заявитель, — и очередь ИТ-службы гаснет независимо от того, нашёлся аппарат там, где
     * заявили, или в третьем месте (находка Н6).
     *
     * Без заявки бессмысленно и схемой не принимается: подтверждать нечего, если не сказано, чьё
     * заявление разбирали. Перемещение по заявке при этом бывает и служебным («увезли в сервис»),
     * поэтому флаг ставит человек галочкой, а не выводится из наличия `serviceRequestId`.
     */
    confirmsDeclaredPlace: z.boolean().optional().default(false),
  })
  .refine((v) => !officeEquipmentStateNeedsNote(v.state) || !!v.stateNote, {
    message: 'Уточните, где именно находится техника',
    path: ['stateNote'],
  })
  .refine((v) => !v.confirmsDeclaredPlace || !!v.serviceRequestId, {
    message: 'Подтверждение места относится к заявке',
    path: ['confirmsDeclaredPlace'],
  });
export type MoveOfficeEquipmentInput = z.infer<typeof moveOfficeEquipmentSchema>;

export interface OfficeEquipmentDto {
  id: string;
  type: OfficeEquipmentTypeRefDto;
  /**
   * The model from the directory (ADR 0132). Since release B the column is `NOT NULL` and the
   * server always sends it. The type still admits the two release A states — absent (a route not yet
   * moved to models) and `null` (a card without a link) — because portal fixtures and readers were
   * written against them; narrowing the type is its own portal step, and until then a reader treats
   * both as "no model" exactly as before.
   */
  model?: OfficeEquipmentModelRefDto | null;
  /** Имя модели: с выпуска A — копия `model.name`, которую ведёт база (Р3), а не ввод человека. */
  name: string;
  /**
   * Характеристики модели — «цветной или чёрно-белый» второй строкой в колонке «Тип»
   * (план `docs/office-equipment-specs-plan.md`).
   *
   * Counted by the card's **type**, with the values taken from its model. A model that has no value
   * for a spec gives `value: null` — the question "is it a colour device" is legitimate, the answer
   * is unknown, "n/a" (Р9). An empty array means something else: this type asks no specs at all.
   */
  specs: OfficeEquipmentModelSpecDto[];
  serialNumber: string;
  inventoryNumber: string;
  object: OfficeEquipmentObjectRefDto;
  /** Отдел-владелец; `null` — не закреплена (такую единицу и надо разметить). */
  department: OfficeEquipmentDepartmentRefDto | null;
  location: string;
  /** Где единица находится физически и уточнение к этому (Р61). */
  state: OfficeEquipmentState;
  stateNote: string;
  purchasedOn: string | null;
  warrantyUntil: string | null;
  comment: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  /**
   * История обслуживания и гарантии ремонтов (§8.2). Поле необязательное, и это содержательно:
   * его отсутствие означает «не положено видеть» (у смотрящего нет `serviceRequests.read`), а не
   * «ремонтов не было» — последнее выражается пустым массивом. Пустой массив вместо отсутствия
   * заставил бы портал рисовать раздел «Обслуживание — ничего» тому, кому модуль вообще закрыт.
   *
   * Приходит только в карточке (`GET /office-equipment/:id`): в списке справочника этот срез
   * означал бы подзапрос на каждую строку ради данных, которых в списке не видно.
   */
  serviceHistory?: OfficeEquipmentServiceEntryDto[];
  /**
   * Display numbers of the open (not accepted, not cancelled) requests on this unit that the viewer
   * may see, in number order (ADR 0169, E5b). The deactivation warning names them: a current-state
   * guard, not history, so it has no limit, unlike the card section (five rows of the requests
   * block) and the `serviceHistory` slice (ten rows, removed by E6).
   *
   * Optional for the same reason as `serviceHistory`: absent means "not yours to see" (no
   * `serviceRequests.read`), and also comes from a server older than E5b during a deploy window or
   * after a rollback; an empty list means "no open request visible to you". It is never "no open
   * request at all": it is narrowed by the request scope, which for department roles differs from
   * the directory scope.
   *
   * Only the card (`GET /office-equipment/:id`) sends it.
   */
  openServiceRequestNumbers?: string[];
  /**
   * Чем этот аппарат заправлять (Р15): расходники, привязанные к его модели, — код, наименование,
   * цвет и остаток. Порядок — по наименованию; погашенные позиции сюда не попадают: их больше не
   * покупают, и предлагать их тому, кто пришёл за картриджем, значит звать к пустой полке.
   *
   * Права своего у блока нет — он отдаётся по `officeEquipment.read`, тому же, по которому открыта
   * сама карточка: «чем заправлять» это часть эксплуатации техники, а не складская тайна.
   * Историю обслуживания рядом это не касается — она про деньги и работы, и потому просит
   * `serviceRequests.read` со своей областью.
   *
   * Приходит только в карточке (`GET /office-equipment/:id`): в списке справочника этот срез
   * означал бы запрос на каждую строку ради данных, которых в списке не видно. Поэтому отсутствие
   * поля значит «этот ответ такого среза не содержит», а не «расходников нет» — последнее
   * выражается пустым массивом, и он же приходит у карточки без модели (окно выпуска A): вопрос
   * «чем заправлять» законен всегда, а ответ на него у безмодельной карточки просто пуст.
   */
  consumables?: OfficeEquipmentConsumableRefDto[];
}

/**
 * Как единица называется в списке и в подсказках: модель плюс номер, которым её опознают. Функция
 * общая для портала и писем — «Kyocera M3145 · инв. 0012345» должно читаться одинаково везде.
 */
export function officeEquipmentTitle(
  item: Pick<OfficeEquipmentDto, 'name' | 'inventoryNumber' | 'serialNumber'>,
): string {
  const num = item.inventoryNumber
    ? `инв. ${item.inventoryNumber}`
    : item.serialNumber
      ? `SN ${item.serialNumber}`
      : '';
  return num ? `${item.name} · ${num}` : item.name;
}

// ── Проекция справочника для выбора предмета заявки (план
// `docs/office-equipment-request-subject-plan.md`, Р1–Р3) ──
//
// СВОЯ ПРОЕКЦИЯ И СВОИ ДВЕ РУЧКИ, а не признак «выбираю предмет заявки» у списка и карточки. Это
// решение ревью, и причин у него три, каждой хватило бы поодиночке:
//
//   1. состав ответа. `OfficeEquipmentDto` несёт комментарий, дату покупки, состояние с уточнением,
//      характеристики модели, а карточка сверх того — расходники и историю обслуживания. Открыть
//      это по всему парку каждому заявителю значило бы не «расширить поиск», а раздать учётные
//      данные компании;
//   2. общий кэш. Дочитка выбранного писала бы в ту же запись react-query, которой живёт обычная
//      карточка, и запись вне области приезжала бы на обычный экран из кэша;
//   3. чужие потребители. Выдачу справочника спрашивают ещё двое — объединение кандидата и окно
//      расходников заявки, — и признак у общей функции однажды доехал бы и к ним.
//
// Отсюда правило, которое здесь и держится: обычные список, карточка, их DTO и ключи не меняются
// ни строкой, а у этой проекции нет ни одного поля сверх того, что рисуют поле выбора и плашка.

/**
 * Со скольких символов поиск выходит за область учётки (Р3).
 *
 * Порог общий у сервера и портала намеренно: сервер им решает, сужать ли выдачу областью, а форма
 * — что написать под пустым списком («наберите три символа, чтобы искать по всему парку»). Два
 * числа разошлись бы на первой же правке, и портал обещал бы поиск, которого сервер не делает.
 *
 * Почему вообще порог, а не «ищем везде всегда»: набор короче трёх символов — это не поиск, а
 * праздный просмотр парка компании, и открывать его каждому заявителю не за что. Поиску по номеру
 * порог не мешает — номера длиннее.
 */
export const OFFICE_EQUIPMENT_SELECTOR_SEARCH_MIN = 3;

/**
 * Что спрашивают у выдачи для выбора предмета заявки: страница и набранное.
 *
 * ПРАВИЛА СТРАНИЦЫ БЕРУТСЯ У ОБЩЕЙ СХЕМЫ (тот же приём, что у ленты остатка расходника): номер с
 * единицы, размер — только из общего перечня `PAGE_SIZES`. Своя пара полей выглядела бы такой же,
 * но позволила бы попросить страницу в десять тысяч строк, то есть выгрузить весь парк одним
 * запросом мимо правила, общего для всех списков портала.
 *
 * А `sortBy` и `sortOrder` из неё выброшены: порядок здесь задан жёстко — по наименованию, — и
 * поле сортировки, которое сервер обязан игнорировать, было бы обещанием, которого он не
 * выполняет. Отборов у выдачи нет вовсе по той же причине: спрашивают её из одного поля формы, и
 * единственный вопрос к ней — «как называется аппарат».
 */
export const officeEquipmentSelectorQuerySchema = baseListQuery([]).pick({
  page: true,
  pageSize: true,
  search: true,
});
export type OfficeEquipmentSelectorQuery = z.infer<typeof officeEquipmentSelectorQuerySchema>;

/**
 * Единица справочника глазами формы заявки: подпись, реквизиты снимка, место и два ответа об
 * области (Р1, Р2).
 *
 * Ни комментария, ни закупки, ни состояния, ни характеристик, ни расходников, ни истории — и это
 * не «пока не понадобилось», а граница: по этой проекции виден весь активный парк компании, а по
 * карточке — только своя область.
 *
 * Объект и отдел приходят теми же ссылками, что и в карточке (`OfficeEquipmentObjectRefDto`,
 * `OfficeEquipmentDepartmentRefDto`), а не готовой строкой подписи. Готовая строка была бы вторым
 * написанием площадки в портале: «СМР-2 · Стройка на Ленина» собирают из кода и названия и список
 * техники, и окно перемещения, и письма, — и собранная сервером специально для этого поля она
 * разошлась бы с ними на первой же правке формата. Код при этом не украшение: названия объектов
 * повторяются («Склад»), и без него плашка «аппарат стоит на другой площадке» не называет, на
 * какой именно.
 */
export interface OfficeEquipmentRequestOptionDto {
  id: string;
  /** Имя модели — им аппарат и подписан (`officeEquipmentTitle`). */
  name: string;
  /** Оба номера: по ним ищут (бухгалтерия по инвентарному, сервис по серийному) и ими опознают. */
  serialNumber: string;
  inventoryNumber: string;
  /** Где стоит: «Где стоит» под полем и текст плашки о чужой площадке. */
  object: OfficeEquipmentObjectRefDto;
  /** За каким отделом числится; `null` — не размечена. Плашка отдельской роли читается по нему. */
  ownerDepartment: OfficeEquipmentDepartmentRefDto | null;
  /** Кабинет — третий способ назвать аппарат вслух («принтер в 214-м»). */
  location: string;
  /** По нему форма считает, не гарантийное ли обращение. */
  warrantyUntil: string | null;
  /**
   * Эксплуатируется ли. В выдаче все строки активны — заявку заводят на технику, которая стоит в
   * кабинете, — а вот дочитка по id отдаёт и погашенную (Р3): устаревший выбор должен быть виден
   * как устаревший, а не исчезнуть в «не найдено».
   */
  isActive: boolean;
  /**
   * Попадает ли карточка в область учётки ЦЕЛИКОМ — тот же ответ, что даёт список справочника.
   * `false` означает «этот аппарат вы бы в своём справочнике не увидели», и форма показывает по
   * нему предупреждение.
   */
  inOwnScope: boolean;
  /**
   * Попадает ли в область учётки ОБЪЕКТ карточки (Р2).
   *
   * Второй признак заведён ради одного случая, и без него он неразличим: у отдельской роли аппарат
   * бывает чужим ТОЛЬКО по владельцу, стоя при этом на своей площадке. Тогда `inOwnScope: false`,
   * а поправку «стоит на другом объекте» включать нельзя — объект и так тот самый. Пара
   * «`inOwnScope: false`, `objectInOwnScope: true`» и означает «чужой отдел, своя площадка».
   */
  objectInOwnScope: boolean;
}

/**
 * Состав проекции, видимый в момент выполнения.
 *
 * Зачем он вообще нужен. Поля интерфейса стираются при сборке, а `apps/api/tsconfig.json` не
 * включает каталог `test/` — то есть проверка «в проекции нет тяжёлых полей», написанная типами в
 * тесте, не проверялась бы ничем и зеленела бы всегда. Перечень даёт тесту то, что можно
 * посмотреть глазами прогона.
 *
 * Обе стороны держит компилятор: тип записи требует решения на КАЖДОЕ поле DTO — забытое поле
 * компилируется в ошибку «отсутствует свойство», лишнее имя — в «такого свойства нет». Поэтому
 * перечень не может молча отстать от проекции, а проекция — обрасти полем мимо перечня.
 */
const requestOptionFields: Record<keyof OfficeEquipmentRequestOptionDto, true> = {
  id: true,
  name: true,
  serialNumber: true,
  inventoryNumber: true,
  object: true,
  ownerDepartment: true,
  location: true,
  warrantyUntil: true,
  isActive: true,
  inOwnScope: true,
  objectInOwnScope: true,
};

/** Имена полей проекции — ими сверяется её состав (см. `requestOptionFields`). */
export const OFFICE_EQUIPMENT_REQUEST_OPTION_FIELDS = Object.keys(
  requestOptionFields,
) as readonly (keyof OfficeEquipmentRequestOptionDto)[];
